import React, { useRef, useState } from "react";
  import { Box, Button, Tooltip, Typography } from "@mui/material";
  import AlertManager from "../../shared/Alert/Alert";
  import { uploadLandedCostFile } from "../../API/landedCostVendorUploadApi";
  import { downloadTemplateFile } from "../../API/landedCostVendorDownloadTemplateFileApi";
  import LandedCostUploadHistoryTable from "../../shared/LandedCostUploadHistoryTable/LandedCostUploadHistoryTable";
  import { useLandedCostVendorHistory } from "./LandedCostUpload.hooks";
import {
    ACCEPTED_MIME_TYPES,
    FILE_ACCEPTED_TYPES,
    MAX_FILE_SIZE_MB,
  } from "./LandedCostUploadConstants";
  import classes from "./LandedCostUpload.module.css";

  const SUCCESS_MESSAGE = "File received. You will get an email after processing.";

  const LandedCostUpload = () => {
    const fileInputRef = useRef(null);
    const { uploadHistory, fetchUploadHistory, loading } = useLandedCostVendorHistory();

    const [selectedFileName, setSelectedFileName] = useState("");

    const resetFileInput = () => {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setSelectedFileName("");
    };

    const handleFileChange = (event) => {
      const file = event?.target?.files?.[0];
      setSelectedFileName(file ? file.name : "");
    };

    const handleSubmit = async (event) => {
      event.preventDefault();
      const file = fileInputRef.current?.files?.[0];

      if (!file) {
        AlertManager.error("Choose a spreadsheet to upload.", { contactSupport: false });
        resetFileInput();
        return;
      }
      const isExcel = ACCEPTED_MIME_TYPES.includes(file.type) || /\.xls[x]?$/i.test(file.name);
      if (!isExcel) {
        AlertManager.error("Only .xlsx or .xls files are supported.", { contactSupport:
  false });
        resetFileInput();
        return;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        AlertManager.error(`File size exceeds ${MAX_FILE_SIZE_MB} MB.`, { contactSupport:
  false });
        resetFileInput();
        return;
      }

      let successShown = false;
      const showSuccess = () => {
        if (!successShown) {
          successShown = true;
          AlertManager.success(SUCCESS_MESSAGE, { contactSupport: false });
        }
      };
      const successTimeoutId = setTimeout(showSuccess, 1000);

      try {
        const response = await uploadLandedCostFile({ file, uploadType: "VENDOR" });
        if (!response) {
          clearTimeout(successTimeoutId);
          return;
        }

        if (response.status === 200 || response.status === 201) {
          clearTimeout(successTimeoutId);
          showSuccess();
          fetchUploadHistory();
          event.target.reset();
          setSelectedFileName("");
        } else {
          clearTimeout(successTimeoutId);
          const payload = await response.json().catch(() => ({}));
          AlertManager.error(payload?.message || "There were issues while uploading the file.");
          resetFileInput();
        }
      } catch (error) {
        clearTimeout(successTimeoutId);
        AlertManager.error("There were issues while uploading the file.");
        resetFileInput();
      }
    };

    return (
      <section className={classes.body}>
        <header className={classes.heading}>Upload vendor to container mapping for landed cost</header>

        <form className={classes.container} onSubmit={handleSubmit}>
          <Box className={classes.fieldRow}>
            <Button component="label" variant="outlined" size="small">
              Choose File
              <input
                hidden
                ref={fileInputRef}
                type="file"
                accept={FILE_ACCEPTED_TYPES}
                data-testid="vendor-file-input"
                onChange={handleFileChange}
              />
            </Button>
            <Typography variant="body2" className={classes.fileNameDisplay}>
              {selectedFileName || "No file selected"}
            </Typography>
            <Typography variant="body2" className={classes.helperText}>
              Accepted formats: .xlsx / .xls Max size {MAX_FILE_SIZE_MB} MB
            </Typography>
          </Box>

          <Box className={classes.actions}>
            <Button className={classes.uploadBtn} type="submit" data-testid="vendor-upload-
  btn">
              Upload
            </Button>
            <Tooltip title="Download the standardized vendor template">
              <Button className={classes.uploadBtn} variant="text"
  onClick={() => downloadTemplateFile('VENDOR')}>
                Download Template
              </Button>
            </Tooltip>
          </Box>
        </form>

        <div className={classes.hLine} />

        <div className={classes.fileuploadHistory}>
          <LandedCostUploadHistoryTable
            uploadHistory={uploadHistory}
            loading={loading}
            onRefresh={fetchUploadHistory}
            enableDelete={false}
          />
        </div>
      </section>
    );
  };

  export default LandedCostUpload;
